// Lernhilfen neben dem Brett: Hinweis, Zug zurück, Fehler sofort zeigen, Urteil über den eigenen Zug,
// Eröffnung mit den Zügen aus der Bibliothek; nach der Partie „Lerne aus Fehlern“ (views/playtrain.ts).
import { h, type VNode } from 'snabbdom';
import type { LibraryMove } from '../coachapi';
import { t } from '../i18n';
import type { PlayCtrl } from '../playctrl';
import { GLYPH } from './review';

export const WORD = { inaccuracy: 'Ungenauigkeit', mistake: 'Fehler', blunder: 'Grober Fehler' } as const;

const num = (n: number) => n.toLocaleString(document.documentElement.lang || 'de');
const btn = (label: string, on: () => void, disabled = false) => h('button.btn', { attrs: { disabled }, on: { click: on } }, t(label));

export function coachPanel(c: PlayCtrl): VNode {
  const k = c.coach;
  const myTurn = c.started && !c.result && !c.thinking && !k.checking && !k.pending && c.pos.turn === c.me;
  return h('div.setup.coach', [
    h('div.hd', h('h3', t('Lernhilfen'))),
    h('div.fld', [
      h('div.row', [
        h('div.row.btns', [btn('Hinweis', () => void c.askHint(), !myTurn), btn('Zug zurück', c.takeBack, !c.canTakeBack)]),
        h('label.chk', [
          h('input', { attrs: { type: 'checkbox' }, props: { checked: k.instant }, on: { change: (e: Event) => k.setInstant((e.target as HTMLInputElement).checked) } }),
          ' ' + t('Fehler sofort zeigen'),
        ]),
      ]),
    ]),
    verdict(c),
    opening(c),
    learn(c),
  ]);
}

function verdict(c: PlayCtrl): VNode | null {
  const k = c.coach;
  if (k.checking) return h('div.fld', h('p.muted.small', t('Der Coach prüft deinen Zug …')));
  const p = k.pending;
  if (!p) return null;
  return h('div.fld.verdict', [
    h('p', [h(`span.${p.judgement}`, `${t(WORD[p.judgement])}: ${p.playedSan}${GLYPH[p.judgement]}`), ' ' + t('Besser war {m}.', { m: p.bestSan })]),
    h('div.row.btns', [btn('Nochmal versuchen', c.retry), btn('Weiterspielen', () => c.carryOn())]),
  ]);
}

function opening(c: PlayCtrl): VNode | null {
  const o = c.coach.opening;
  if (!c.started || !o || c.coach.openingFen !== c.fen) return null;
  const lib = o.library;
  if (!o.name && !o.known && !lib.games) return null;
  return h('div.fld.opening', [
    h('div.row', [
      h('span', o.name ? `${o.eco} · ${o.name}` : t('Eröffnung')),
      h('span.muted.small', o.known ? t('Theorie') : t('außerhalb der Theorie')),
    ]),
    !lib.ready ? h('p.muted.small', t('Die Partien der Bibliothek werden ausgewertet …')) : null,
    lib.games ? h('div.lib', [
      h('p.muted.small', t('In der Bibliothek von hier gespielt ({n} Partien)', { n: num(lib.games) })),
      ...lib.moves.map(libRow),
    ]) : null,
  ]);
}

/** Zug mit Anzahl und Ergebnisbalken Weiß gewinnt · Remis · Schwarz gewinnt */
function libRow(m: LibraryMove): VNode {
  const pct = (n: number) => Math.round((100 * n) / Math.max(1, m.white + m.draws + m.black));
  const part = (cls: string, n: number) => h(`i.${cls}`, { style: { flexGrow: String(n) } }, pct(n) >= 14 ? `${pct(n)} %` : '');
  const title = t('Weiß {w} % · Remis {d} % · Schwarz {b} %', { w: pct(m.white), d: pct(m.draws), b: pct(m.black) });
  return h('div.lib-row', [
    h('span.san', m.san),
    h('span.n', num(m.games)),
    h('span.wdb', { attrs: { title, 'aria-label': title } }, [part('w', m.white), part('d', m.draws), part('b', m.black)]),
  ]);
}

function learn(c: PlayCtrl): VNode | null {
  const n = c.coach.slips.length;
  if (!c.result || !n) return null;
  return h('div.fld', [
    h('div.row', [
      h('span', n === 1 ? t('1 Fehler in dieser Partie') : t('{n} Fehler in dieser Partie', { n })),
      btn('Lerne aus Fehlern', () => c.startTraining()),
    ]),
  ]);
}
